import { FormNames } from "./CESTypes";
import * as componentFactory from "./ComponentFactory";
import { ValueComponent, Validation } from "./components/Base";
import RecaptchaFactory from "./components/RecaptchaFactory";
import { SteppedForm, StepIndicator, StepperNavigation } from "./components/Stepper";
import { StepDescriptor, getRegistrationFormSubStep } from "./StepperContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FormField = {
  type: string;
  name: string;
  value?: any;
  prefixField?: {
    name: string;
    value?: any;
  };
  [key: string]: any;
};

type ComponentBuilder = (
  field: FormField,
  formId: string,
  formName: string
) => {
  row: HTMLElement;
  controls: ValueComponent[];
  validation?: Validation;
};

type BuiltStep = {
  id: string;
  fields: FormField[];
};

type FormControls = {
  all: ValueComponent[];
  byName: Map<string, ValueComponent>;
};

type FormState = {
  controls: FormControls;
  validations: Validation[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getComponentBuilder = (fieldType: string): ComponentBuilder | undefined =>
  (componentFactory as unknown as Record<string, ComponentBuilder>)[fieldType];

const renderField = (
  field: FormField,
  formId: string,
  formName: string,
  state: FormState,
  container: HTMLElement
): void => {
  const buildComponent = getComponentBuilder(field.type);
  if (!buildComponent) {
    console.error(`Invalid field type: ${field.type}`);
    return;
  }

  const { row, controls, validation } = buildComponent(field, formId, formName);
  container.appendChild(row);
  controls.forEach((c) => state.controls.all.push(c));
  if (validation) state.validations.push(validation);
}

const attachPreventSubmit = (form: HTMLFormElement, onSubmit: Function): void => {
  const handler = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (event.type === "keydown") onSubmit();
    }
  };
  form.addEventListener("keydown", handler);
  form.addEventListener("keyup", handler);
  form.addEventListener("keypress", handler);
}

const attachStepIndicator = (stepper: SteppedForm): void => {
  const indicatorsContainer = document.getElementById("step-indicators");
  if (indicatorsContainer) new StepIndicator(indicatorsContainer, stepper);
}

// ---------------------------------------------------------------------------
// Step builder for registration (sub-stepped) forms
// ---------------------------------------------------------------------------

const resolveBuiltSteps = (
  formId: string,
  fields: FormField[],
  formSteps: StepDescriptor[]
): BuiltStep[] => {
  const builtSteps: BuiltStep[] = formSteps.map((step) => {
    const fieldNames = step.substep
      ? getRegistrationFormSubStep(step.substep)?.fieldNames ?? []
      : [];
    return {
      id: `${formId}-${step.substep}`,
      fields: fields.filter((f) => fieldNames.includes(f.name)),
    };
  });

  const renderedNames = new Set(builtSteps.flatMap((s) => s.fields.map((f) => f.name)));

  // Append any unmapped fields to step 1 so no required control is dropped.
  if (builtSteps.length && renderedNames.size < fields.length) {
    const firstStepNames = new Set(builtSteps[0].fields.map((f) => f.name));
    builtSteps[0].fields = fields.filter(
      (f) => firstStepNames.has(f.name) || !renderedNames.has(f.name)
    );
  }

  return builtSteps;
}

const buildSteppedForm = (
  form: HTMLFormElement,
  formId: string,
  formName: string,
  fields: FormField[],
  onSubmit: Function,
  formSteps: StepDescriptor[],
  totalSteps: number,
  globalOffset: number
): FormState => {
  const state: FormState = {
    controls: { all: [], byName: new Map() },
    validations: [],
  };

  const builtSteps = resolveBuiltSteps(formId, fields, formSteps);
  const stepper = new SteppedForm(builtSteps, totalSteps, globalOffset);
  attachStepIndicator(stepper);

  const stepContainers: HTMLElement[] = [];
  const stepValidations: Validation[][] = [];

  builtSteps.forEach((step, index) => {
    const section = document.createElement("section");
    section.dataset.stepId = step.id;
    section.hidden = index !== 0;

    stepValidations[index] = [];

    const stepState: FormState = {
      controls: state.controls,
      validations: stepValidations[index],
    };

    step.fields.forEach((field) => renderField(field, formId, formName, stepState, section));

    // Keep global validations in sync so final submit validates every step.
    stepValidations[index].forEach((v) => state.validations.push(v));

    stepContainers.push(section);
    form.append(section);
  });

  const nav = new StepperNavigation(stepper, {
    isStepValid: (localIndex) =>
      stepValidations[localIndex]?.every((v) => v.isValid()) ?? true,
    onSectionChange: (localIndex) => {
      stepContainers.forEach((section, i) => { section.hidden = i !== localIndex; });
    },
    buttons: {
      prev: componentFactory.prevBtn(),
      next: componentFactory.nextBtn(),
      submit: componentFactory.submit(formId, formName, onSubmit),
    },
  });
  form.append(nav.container);

  return state;
}

// ---------------------------------------------------------------------------
// Flat (single-page) form builder
// ---------------------------------------------------------------------------

const buildFlatForm = (
  form: HTMLFormElement,
  formId: string,
  formName: string,
  fields: FormField[],
  onSubmit: Function,
  stepDescriptor?: { totalSteps: number; globalOffset: number }
): FormState => {
  const state: FormState = {
    controls: { all: [], byName: new Map() },
    validations: [],
  };

  if (stepDescriptor) {
    const stepper = new SteppedForm(
      [{ id: `${formId}-single`, fields: [] } as any],
      stepDescriptor.totalSteps,
      stepDescriptor.globalOffset
    );
    attachStepIndicator(stepper);
  }

  fields.forEach((field) => renderField(field, formId, formName, state, form));

  form.appendChild(componentFactory.submit(formId, formName, onSubmit));

  return state;
}

// ---------------------------------------------------------------------------
// Return object factory
// ---------------------------------------------------------------------------

const buildFormReturn = (
  form: HTMLFormElement,
  fields: FormField[],
  state: FormState
) => {
  const { all: controls, byName: controlsByName } = state.controls;
  const { validations: formValidations } = state;

  // Populate byName now that all controls have been collected.
  controls.forEach((c) => controlsByName.set(c.name, c));

  return {
    getDOM: () => form,

    isValid: () => {
      const valid = formValidations.every((v) => v.isValid());
      if (!valid) {
        const messages = document.querySelectorAll(".validation-error > p[style*='block']");
        if (messages.length > 0) {
          window.scrollTo(0, (messages[0] as HTMLElement).offsetTop);
        }
      }
      return valid;
    },

    getValues: async () => {
      const result: Record<string, any> = {};
      for (const control of controls) {
        result[control.name] = await control.value;
      }
      return result;
    },

    setValues: () => {
      // Assigning to a getter-only `value` throws in strict mode and would
      // abort prefilling the whole form — degrade to a warning instead.
      const trySetValue = (control: ValueComponent, value: any) => {
        try {
          control.value = value;
        } catch (error) {
          console.warn(`Unable to set value for field "${control.name}":`, error);
        }
      };

      fields.forEach((field) => {
        if (field.prefixField?.value != null) {
          const prefixControl = controlsByName.get(field.prefixField.name) as ValueComponent | undefined;
          if (prefixControl) {
            trySetValue(prefixControl, field.prefixField.value);
          }
        }
        if (field.value != null) {
          const control = controlsByName.get(field.name) as ValueComponent | undefined;
          if (control) {
            trySetValue(control, field.value);
          }
        }
      });
    },

    reset: () => RecaptchaFactory.getInstance().reset(),

    dispose: () => {
      controls.forEach((c) => c.dispose());
      formValidations.forEach((v) => v.dispose());
    },
  };
}

// ---------------------------------------------------------------------------
// Main form builder
// ---------------------------------------------------------------------------

const buildForm = (
  formId: string,
  formName: string,
  fields: FormField[],
  onSubmit: Function,
  steps?: StepDescriptor[]
) => {
  const form = document.createElement("form");
  form.setAttribute("name", formId);
  form.setAttribute("class", formId);
  attachPreventSubmit(form, onSubmit);

  let state: FormState;

  if (steps) {
    const formSteps = steps.filter((s) => s.form === formName);
    const totalSteps = formSteps[0]?.totalSteps ?? steps[0]?.totalSteps ?? steps.length;
    const globalOffset = formSteps.length > 0 ? formSteps[0].step - 1 : 0;
    const hasSubsteps = formName === FormNames.Registration && formSteps.some((s) => s.substep);

    if (hasSubsteps) {
      state = buildSteppedForm(
        form, formId, formName, fields, onSubmit,
        formSteps, totalSteps, globalOffset
      );
    } else {
      state = buildFlatForm(form, formId, formName, fields, onSubmit, { totalSteps, globalOffset });
    }
  } else {
    state = buildFlatForm(form, formId, formName, fields, onSubmit);
  }

  return buildFormReturn(form, fields, state);
};

export default buildForm;